import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { NewsCard } from '@/components/news';
import { NavyAtmosphere, ScrollReveal, SplitReveal, TextStagger } from '@/components/motion';
import { EnquiryForm } from '@/components/sections';
import { NEWS, NEWS_CATEGORIES, type NewsCategory } from '@/data/news';
import { cn } from '@ibas/ui';

/**
 * NewsPage (/news) — "News & Events". A navy, motion-led hero opens the page,
 * then a category filter (driven by the ?category= search param so the mega-nav
 * can deep-link) narrows a responsive grid of every current article, newest
 * first. Each card links to its /news/:slug detail page. The shared enquiry
 * form closes the page.
 *
 * All content is read from @/data/news (verified live IBAS newsroom); nothing
 * is invented. Reduced-motion safe throughout.
 */
export function NewsPage() {
  const [params, setParams] = useSearchParams();
  const raw = params.get('category');
  const active: NewsCategory | 'All' =
    raw && (NEWS_CATEGORIES as readonly string[]).includes(raw) ? (raw as NewsCategory) : 'All';

  const articles = useMemo(
    () => (active === 'All' ? NEWS : NEWS.filter((a) => a.category === active)),
    [active],
  );

  const select = (category: NewsCategory | 'All') => {
    const next = new URLSearchParams(params);
    if (category === 'All') next.delete('category');
    else next.set('category', category);
    setParams(next, { replace: true });
  };

  const filters: readonly (NewsCategory | 'All')[] = ['All', ...NEWS_CATEGORIES];

  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative isolate flex min-h-[62vh] items-center overflow-hidden bg-brand-navy">
        <NavyAtmosphere />
        <div className="relative z-10 mx-auto w-full max-w-6xl px-6 py-24">
          <div className="max-w-2xl">
            <TextStagger
              text="News & Events"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.05] text-white md:text-6xl"
            >
              The latest from IBAS.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/70">
                Rankings and accreditations, graduations, campus moments and announcements — a
                running record of life and milestones at the International Business Academy of
                Switzerland.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* ── FILTER + GRID ────────────────────────────────────────────────── */}
      <section className="bg-background py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl px-6">
          {/* Category filter */}
          <div className="flex flex-wrap gap-2.5" role="group" aria-label="Filter news by category">
            {filters.map((category) => {
              const isActive = category === active;
              return (
                <button
                  key={category}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => select(category)}
                  className={cn(
                    'rounded-full border px-5 py-2 text-sm font-medium transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:outline-none motion-reduce:transition-none',
                    isActive
                      ? 'border-brand-navy bg-brand-navy text-white'
                      : 'border-border bg-background text-foreground hover:border-brand-gold/40 hover:text-brand-navy',
                  )}
                >
                  {category}
                </button>
              );
            })}
          </div>

          {/* Article grid — keyed by active so cards re-reveal on filter change. */}
          <ScrollReveal
            key={active}
            from="up"
            stagger={0.06}
            className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
          >
            {articles.map((article) => (
              <NewsCard key={article.slug} article={article} />
            ))}
          </ScrollReveal>

          {articles.length === 0 && (
            <p className="mt-10 text-sm text-muted-foreground">No articles in this category yet.</p>
          )}
        </div>
      </section>

      <EnquiryForm />
    </div>
  );
}
